import { AppointmentsEmptyCalendar } from '@avanta/shared/components/AppointmentsEmptyCalendar';
import { SlotButton } from '@avanta/shared/components/SlotButton';
import { Text } from '@avanta/shared/components/Text';
import { Calendar } from '@avanta/shared/components/ui/Calendar';
import { Skeleton } from '@avanta/shared/components/ui/Skeleton';
import { APPOINTMENTS_SCHEDULE_DAYS, LOCIZE_NS } from '@avanta/shared/constants/common';
import {
  checkIsDayDisabled,
  getFirstAvailableDate,
  getTimeSlotsForDate,
} from '@avanta/shared/utils/appointments';
import {
  formatDateTimeUnified,
  extractTimeFromUnified,
  formatDateISO,
} from '@avanta/shared/utils/date';
import { addDays, isSameMonth, startOfDay } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { hasFixedPsychologist } from 'pages/AppointmentsReserve/utils';
import { useSessionAvailability } from '../hooks/useSessionAvailability';
import { AppointmentsCalendarSection } from './AppointmentsCalendarSection';

export const AppointmentsCalendar = (): React.ReactElement => {
  const { control, setValue } = useFormContext<AppointmentsReserveFormData>();
  const [dateTime, flow] = useWatch({
    control,
    name: [AppointmentsReservePayload.dateTime, AppointmentsReservePayload.flow],
  });
  const { availableDates, isLoading, isFetching } = useSessionAvailability();
  const today = useMemo(() => startOfDay(new Date()), []);
  const endDate = useMemo(() => startOfDay(addDays(today, APPOINTMENTS_SCHEDULE_DAYS)), [today]);
  const [prevAvailableDates, setPrevAvailableDates] = useState(availableDates);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const firstAvailableDate = getFirstAvailableDate(availableDates);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(firstAvailableDate);
  const [calendarMonth, setCalendarMonth] = useState(firstAvailableDate ?? today);

  if (prevAvailableDates !== availableDates) {
    setPrevAvailableDates(availableDates);
    if (!selectedDate || !availableDates[formatDateISO(selectedDate)]?.length) {
      setSelectedDate(firstAvailableDate);
      setCalendarMonth(firstAvailableDate ?? today);
    }
  }

  const selectedTime = dateTime ? extractTimeFromUnified(dateTime) : '';

  const allAvailableSlots = useMemo(() => {
    if (!selectedDate) return [];
    return getTimeSlotsForDate(selectedDate, availableDates).timeSlots;
  }, [selectedDate, availableDates]);
  const hasAvailableDates = Object.keys(availableDates).length > 0;
  const hasAvailableSlots = allAvailableSlots.some(slot => !slot.disabled);

  const handleOnSelectTime = (time: string): void => {
    const isDeselecting = time === selectedTime;
    if (!hasFixedPsychologist(flow)) {
      setValue(AppointmentsReservePayload.psychologist, undefined);
    }
    setValue(
      AppointmentsReservePayload.dateTime,
      !isDeselecting && selectedDate ? formatDateTimeUnified(selectedDate, time) : ''
    );
  };

  const isDayDisabled = useCallback(
    (day: Date): boolean => checkIsDayDisabled(day, availableDates, today, endDate),
    [availableDates, today, endDate]
  );

  const handleOnSelectDay = (date: Date): void => {
    setSelectedDate(date);
    setCalendarMonth(date);
    setValue(AppointmentsReservePayload.dateTime, '');
  };

  if (isLoading || isFetching) {
    return (
      <AppointmentsCalendarSection>
        <Skeleton className="min-h-81 w-full rounded-xl" />
      </AppointmentsCalendarSection>
    );
  }

  if (!hasAvailableDates) {
    return (
      <AppointmentsCalendarSection className="flex min-h-81 items-center justify-center rounded-xl border border-teal-300 p-4">
        <AppointmentsEmptyCalendar variant="emptyDates" className="max-w-138.5" />
      </AppointmentsCalendarSection>
    );
  }

  const renderTimeContent = (): React.ReactElement => {
    if (!hasAvailableSlots) {
      return (
        <AppointmentsEmptyCalendar
          variant="emptySlots"
          className="h-full max-w-52 self-center px-1"
        />
      );
    }

    return (
      <>
        <Text variant="text-sm-medium" className="text-denim-500">
          {t('AppointmentsReserve.Steps.session.dateTime.time.label')}
        </Text>
        <div className="grid grid-cols-3 gap-2 max-w-51 w-full">
          {allAvailableSlots.map(({ time, disabled }) => (
            <SlotButton
              key={time}
              isActive={selectedTime === time}
              disabled={disabled}
              onClick={() => handleOnSelectTime(time)}
            >
              {time}
            </SlotButton>
          ))}
        </div>
      </>
    );
  };

  return (
    <AppointmentsCalendarSection className="flex justify-between h-full gap-8 rounded-xl border border-teal-300 p-6">
      <Calendar
        className="max-w-75 w-full rounded-xl border border-transparent p-0 shrink-0"
        selected={selectedDate}
        month={calendarMonth}
        onMonthChange={setCalendarMonth}
        startMonth={today}
        endMonth={endDate}
        onSelect={handleOnSelectDay}
        disabled={isDayDisabled}
        showOutsideDays
        hideNavigation={isSameMonth(today, endDate)}
      />
      <div className="flex flex-col gap-2 shrink-0 w-full max-w-55">{renderTimeContent()}</div>
    </AppointmentsCalendarSection>
  );
};
